import heyallergyLogoUrl from '../assets/heyallergy-logo.svg';
import { cn } from '../lib/cn';

/** Intrinsic aspect ratio of the mark, so it never distorts. */
const HEYALLERGY_RATIO = 161 / 30;

export interface LogoProps {
  /**
   * `platform` always renders the HeyAllergy mark — use it in the super admin app,
   * which never takes tenant branding (FR-UI-03).
   * `tenant` renders whatever `--logo-url` resolves to, which is the HeyAllergy mark
   * until a tenant overrides that variable (see packages/ui/README.md).
   */
  variant?: 'platform' | 'tenant';
  /** Rendered height in pixels. 36 in the funnel header, 32 in the admin nav. */
  height?: number;
  /** Accessible name. Set it to the clinic's name when rendering a tenant logo. */
  label?: string;
  className?: string;
}

/**
 * The HeyAllergy mark. There is exactly one logo asset on the whole site and it is
 * full-color on white with two `fill="white"` shapes inside (tokens doc §8.1), so it
 * must only ever sit on a white surface. That is one of the two reasons the admin left
 * nav is white rather than dark.
 *
 * The tenant variant is CSS-only: it paints `var(--logo-url)` as a background, so
 * per-tenant branding is a variable assignment on `:root` and needs no React state, no
 * prop drilling and no re-render.
 */
export function Logo({
  variant = 'platform',
  height = 32,
  label = 'HeyAllergy',
  className,
}: LogoProps) {
  if (variant === 'tenant') {
    return (
      <span
        role="img"
        aria-label={label}
        data-testid="logo-tenant"
        className={cn('block bg-contain bg-left bg-no-repeat', className)}
        style={{
          height,
          width: height * HEYALLERGY_RATIO,
          backgroundImage: 'var(--logo-url)',
          aspectRatio: 'var(--logo-aspect)',
        }}
      />
    );
  }

  return (
    <img
      src={heyallergyLogoUrl}
      alt={label}
      width={Math.round(height * HEYALLERGY_RATIO)}
      height={height}
      style={{ height }}
      className={cn('block w-auto', className)}
      data-testid="logo-platform"
    />
  );
}
